import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import InputLabel from "@mui/material/InputLabel";
import MuiLink from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ApiError } from "@bw/shared/api";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import { notifications } from "@/app/notifications";
import {
  createPricingRule,
  updatePricingRule,
  type PricingRule,
  type PricingRuleCreateInput,
  type PricingRuleUpdateInput,
} from "@/libs/ratesApi";
import {
  CHARGE_UNITS,
  CHARGE_UNIT_LABELS,
  DAYS,
  deriveRepeatType,
  EMPTY_FORM,
  EVERY_DAY,
  MEMBERSHIP_SCOPES,
  MEMBERSHIP_SCOPE_LABELS,
  MEMBERSHIP_SCOPE_TRIGGER_LABELS,
  PRICING_SERVICES,
  SERVICE_LABELS,
  WEEKDAYS,
  WEEKEND_DAYS,
  schema,
  type CourtOption,
  type FormValues,
  type PlanOption,
} from "./schema";
import {
  paymentReadinessAction,
  type PaymentReadinessAction,
} from "./payment-readiness-action";
import { ClassOfferingPicker } from "./class-offering-picker";

// Grouped, focused single-rule editor — the rule's full expressive condition set
// organized under FIVE headings (Name / When / Who / Where / Charge) instead of
// a flat field wall, mirroring the staff/roles role-editor language. There is NO
// priority field anywhere. Save → create/update → parent refetch. The FE owns NO
// pricing logic: it forwards money fields as the operator typed them (verbatim
// Decimal strings, no arithmetic) and the backend owns price computation,
// eligibility, precedence, and the cross-field schedule rules. A 403 (or any
// non-422) surfaces as a notifications and the editor stays open; a 422 surfaces the
// backend message inline next to the fields.

export interface RuleEditorProps {
  facilityId: string;
  authorityGeneration: string;
  // The rule being edited, or null when creating a new rule.
  rule: PricingRule | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Called after a successful save so the parent can refetch the list.
  onSaved: () => void | Promise<void>;
  courts: CourtOption[];
  plans: PlanOption[];
}

function ruleToForm(rule: PricingRule): FormValues {
  // Prefill EVERY field from the rule — the form, not a hardcoded default, is
  // authoritative. Editing only the price must not freeze (or wipe) the others.
  return {
    name: rule.name ?? "",
    service: rule.service,
    offering_id: rule.offering_id ?? "",
    base_price: String(rule.base_price ?? ""),
    charge_unit: rule.charge_unit,
    membership_scope: rule.membership_scope,
    repeat_type: rule.repeat_type,
    all_day: Boolean(rule.all_day),
    start_time: rule.start_time ?? "",
    end_time: rule.end_time ?? "",
    start_date: rule.start_date ?? "",
    end_date: rule.end_date ?? "",
    court_ids: rule.court_ids ?? [],
    court_tags: (rule.court_tags ?? []).join(", "),
    membership_plan_ids: rule.membership_plan_ids ?? [],
    // The backend treats an EMPTY days_of_week as "every day" (see
    // PricingRuleCreate.validate_schedule). Prefilling the chips as all-blank
    // for such a rule would (a) violate the new >=1-day requirement the
    // moment the dialog opens and (b) silently NARROW the rule's actual
    // matching behavior to whatever the operator happens to pick first. Map
    // an empty/undefined stored array to "every day selected" so the chips
    // reflect the rule's REAL current matching behavior.
    days_of_week: rule.days_of_week?.length ? rule.days_of_week : EVERY_DAY,
    included_player_count:
      rule.included_player_count != null
        ? String(rule.included_player_count)
        : "",
    extra_player_fee:
      rule.extra_player_fee != null ? String(rule.extra_player_fee) : "",
    entry_fee: rule.entry_fee != null ? String(rule.entry_fee) : "",
  };
}

export function RuleEditor({
  facilityId,
  rule,
  open,
  onOpenChange,
  onSaved,
  courts,
  plans,
  authorityGeneration,
}: RuleEditorProps) {
  const isEdit = rule !== null;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_FORM,
  });

  const service = form.watch("service");
  const allDay = form.watch("all_day");
  const membershipScope = form.watch("membership_scope");
  const selectedDays = form.watch("days_of_week");
  const selectedCourts = form.watch("court_ids");
  const selectedPlans = form.watch("membership_plan_ids");
  const isClasses = service === "classes";

  // "Ends" is Never (end_date = "") vs On date (reveals the end_date input).
  // This is local, presentation-only UI state — it does not exist on the
  // wire; it just decides whether the end_date field is shown, mirroring
  // which of the two is true for the rule's CURRENT end_date. Never clears
  // end_date so a stale date can't survive a Never round-trip.
  const [endsMode, setEndsMode] = useState<"never" | "date">("never");
  const [readinessAction, setReadinessAction] =
    useState<PaymentReadinessAction | null>(null);

  // 422 cross-field schedule errors from the backend surface in the form; all
  // other failures (403, network, 500) go to a notifications (see submit()).
  const formError = form.formState.errors.root?.message ?? null;

  // Seed the form each time the editor opens for a (different) rule / create.
  useEffect(() => {
    if (!open) return;
    form.clearErrors();
    setReadinessAction(null);
    form.reset(rule ? ruleToForm(rule) : EMPTY_FORM);
    setEndsMode(rule?.end_date ? "date" : "never");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rule]);

  // Classes rules and court-scoped fields are MUTUALLY EXCLUSIVE (the backend
  // rejects a classes rule that also carries court_ids/court_tags, and a
  // classes rule needs exactly one offering_id — see validate_classes_rule).
  // Switching the Service select to/from "classes" proactively clears the
  // fields the NEW service doesn't use, so a leftover value from the prior
  // service can never round-trip into a 422 the operator didn't cause. This is
  // idempotent against the seed effect above (a loaded rule's own fields
  // already satisfy its service's invariant, so this is a no-op on open).
  useEffect(() => {
    if (isClasses) {
      if (form.getValues("court_ids").length > 0) {
        form.setValue("court_ids", [], { shouldValidate: true });
      }
      if (form.getValues("court_tags")) {
        form.setValue("court_tags", "", { shouldValidate: true });
      }
      form.setValue("all_day", true, { shouldValidate: true });
      form.setValue("start_time", "", { shouldValidate: true });
      form.setValue("end_time", "", { shouldValidate: true });
      form.setValue("start_date", "", { shouldValidate: true });
      form.setValue("end_date", "", { shouldValidate: true });
      form.setValue("charge_unit", "per_session", { shouldValidate: true });
      form.setValue("included_player_count", "", { shouldValidate: true });
      form.setValue("extra_player_fee", "", { shouldValidate: true });
      form.setValue("entry_fee", "", { shouldValidate: true });
      setEndsMode("never");
    } else if (form.getValues("offering_id")) {
      form.setValue("offering_id", "", { shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClasses]);

  const toggleDay = (value: number) => {
    const next = selectedDays.includes(value)
      ? selectedDays.filter((d) => d !== value)
      : [...selectedDays, value];
    form.setValue("days_of_week", next, { shouldValidate: true });
  };
  const setDays = (days: number[]) => {
    form.setValue("days_of_week", days, { shouldValidate: true });
  };
  const handleEndsModeChange = (mode: "never" | "date") => {
    setEndsMode(mode);
    if (mode === "never") {
      // "Never" IS "no end date" — clear any date the operator had typed so a
      // stray value can't survive a Never round-trip (see submit()).
      form.setValue("end_date", "", { shouldValidate: true });
    }
  };
  const toggleCourt = (id: string) => {
    const next = selectedCourts.includes(id)
      ? selectedCourts.filter((c) => c !== id)
      : [...selectedCourts, id];
    form.setValue("court_ids", next, { shouldValidate: true });
  };
  const togglePlan = (id: string) => {
    const next = selectedPlans.includes(id)
      ? selectedPlans.filter((p) => p !== id)
      : [...selectedPlans, id];
    form.setValue("membership_plan_ids", next, { shouldValidate: true });
  };

  const submit = form.handleSubmit(async (values) => {
    // The FE forwards money fields exactly as the operator typed them — it does
    // NOT compute or transform prices (the backend owns pricing). It only sends
    // the fields the rule actually uses: times only for a windowed rule,
    // membership_plan_ids only for specific_plans; optional fields are omitted
    // when blank. A zero-valued number/fee (included_player_count 0, entry_fee
    // "0.00") is a REAL value, so we omit on emptiness (== "") not truthiness.
    //
    // days_of_week is ALWAYS sent explicit (never gated behind a repeat_type
    // check) — the backend matches purely on days_of_week + time window +
    // date range, where an EMPTY array means "every day". Previously this was
    // only sent for repeat_type "weekly", so the Mon-Fri / Weekends presets
    // silently applied on every day of the week (a live pricing bug). The
    // day chips + quick-picks are the only "When" schedule control now;
    // repeat_type is DERIVED from the chosen day set for storage/summary —
    // it is cosmetic on the backend, never used for matching.
    const tags = values.court_tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const name = values.name.trim();
    const isClassRate = values.service === "classes";
    const body: PricingRuleCreateInput & PricingRuleUpdateInput = {
      service: values.service,
      base_price: values.base_price,
      currency: "USD",
      all_day: isClassRate ? true : values.all_day,
      charge_unit: isClassRate ? "per_session" : values.charge_unit,
      membership_scope: values.membership_scope,
      // A classes rate is offering-keyed and prices at enrollment with
      // day_type="any" and no weekday, so it must NOT carry a day-of-week
      // restriction — the backend validate_classes_rule rejects a non-empty
      // days_of_week (it would leave the class unpriceable). Force "every day"
      // (empty) + repeat_type "never" for classes; the "When" day picker is
      // hidden for them below.
      repeat_type:
        values.service === "classes"
          ? "never"
          : deriveRepeatType(values.days_of_week),
      days_of_week: values.service === "classes" ? [] : values.days_of_week,
      // Rules authored here are tagged source="advanced".
      source: "advanced",
      ...(name ? { name } : {}),
      ...(values.all_day
        ? {}
        : { start_time: values.start_time, end_time: values.end_time }),
      ...(values.membership_scope === "specific_plans"
        ? { membership_plan_ids: values.membership_plan_ids }
        : {}),
      ...(!isClassRate && values.start_date
        ? { start_date: values.start_date }
        : {}),
      ...(!isClassRate && values.end_date ? { end_date: values.end_date } : {}),
      // Classes rules never carry court fields (mirrors validate_classes_rule
      // server-side) — the form already hides + clears these for "classes",
      // this is a defense-in-depth guard against a stale value slipping through.
      ...(values.service !== "classes" && tags.length
        ? { court_tags: tags }
        : {}),
      ...(values.service !== "classes" && values.court_ids.length
        ? { court_ids: values.court_ids }
        : {}),
      // A classes rule is offering-keyed — the ONLY targeting dimension it uses.
      ...(values.service === "classes"
        ? { offering_id: values.offering_id }
        : {}),
      ...(!isClassRate && values.included_player_count !== ""
        ? { included_player_count: Number(values.included_player_count) }
        : {}),
      ...(!isClassRate && values.extra_player_fee !== ""
        ? { extra_player_fee: values.extra_player_fee }
        : {}),
      ...(!isClassRate && values.entry_fee !== ""
        ? { entry_fee: values.entry_fee }
        : {}),
    };
    form.clearErrors("root");
    setReadinessAction(null);
    try {
      if (rule) {
        await updatePricingRule(rule.rule_id, body);
      } else {
        await createPricingRule(facilityId, body);
      }
      await onSaved();
      onOpenChange(false);
    } catch (err) {
      // Error-surfacing split. An authorization/transport failure (403 RBAC,
      // network, 500) arrives as an ApiError with a NON-422 status → surface it
      // as a notifications; the editor stays open and the list is NOT optimistically
      // mutated. A 422 is a backend cross-field validation error
      // (validate_schedule, ge=0 on price) whose message the operator must read
      // next to the fields to correct — surface it IN the form. A non-ApiError
      // (an opaque rejection the client could not classify) is treated the same
      // as a 422: keep the editor open and show the message inline rather than
      // flashing a notifications that vanishes and loses the operator's input.
      if (
        err instanceof ApiError &&
        err.code === "FACILITY_PAYMENTS_UNAVAILABLE"
      ) {
        setReadinessAction(paymentReadinessAction(err));
        form.setError("root", { message: err.message });
      } else if (err instanceof ApiError && err.status !== 422) {
        notifications.error(err.message);
      } else {
        form.setError("root", {
          message: err instanceof Error ? err.message : "Save failed",
        });
      }
    }
  });

  const saving = form.formState.isSubmitting;

  const fieldMessage = (name: keyof FormValues): string | undefined => {
    const message = form.formState.errors[name]?.message;
    return typeof message === "string" ? message : undefined;
  };

  return (
    <Dialog
      aria-describedby="rule-editor-description"
      fullWidth
      maxWidth="md"
      onClose={() => {
        if (!saving) onOpenChange(false);
      }}
      open={open}
      slotProps={{ paper: { sx: { maxHeight: "85vh", maxWidth: 720 } } }}
    >
      <DialogTitle>
        {isEdit ? rule?.name || "Edit rate" : "New rate"}
      </DialogTitle>
      <Box component="form" noValidate onSubmit={submit}>
        <DialogContent dividers>
          <Stack spacing={3}>
            <DialogContentText id="rule-editor-description">
              Rates define what players pay. Pricing is computed by the backend.
            </DialogContentText>

            {formError ? (
              <Alert severity="error">
                <Typography variant="body2">{formError}</Typography>
                {readinessAction ? (
                  <MuiLink
                    component={RouterLink}
                    sx={{ display: "inline-block", fontWeight: 700, mt: 0.5 }}
                    to={readinessAction.href}
                  >
                    {readinessAction.label}
                  </MuiLink>
                ) : null}
              </Alert>
            ) : null}

            {/* ---- Name -------------------------------------------------- */}
            <Box component="fieldset" sx={{ border: 0, m: 0, p: 0 }}>
              <Typography
                component="legend"
                sx={{ fontWeight: 700, mb: 1.5 }}
                variant="body2"
              >
                Name
              </Typography>
              <Controller
                control={form.control}
                name="name"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    error={Boolean(fieldState.error)}
                    fullWidth
                    helperText={fieldState.error?.message}
                    placeholder="e.g. Weekend peak (optional)"
                    size="small"
                    slotProps={{ htmlInput: { "aria-label": "Name" } }}
                  />
                )}
              />
            </Box>

            {/* ---- When -------------------------------------------------- */}
            {!isClasses ? (
              <Box component="fieldset" sx={{ border: 0, m: 0, p: 0 }}>
                <Typography
                  component="legend"
                  sx={{ fontWeight: 700, mb: 1.5 }}
                  variant="body2"
                >
                  When
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      id="rate-repeat-on-label"
                      sx={{ fontWeight: 700, mb: 1 }}
                      variant="body2"
                    >
                      Repeat on
                    </Typography>
                    <Stack
                      aria-labelledby="rate-repeat-on-label"
                      direction="row"
                      role="group"
                      spacing={0.75}
                      sx={{ flexWrap: "wrap", rowGap: 0.75 }}
                    >
                      {DAYS.map((day) => {
                        const active = selectedDays.includes(day.value);
                        return (
                          <Button
                            aria-pressed={active}
                            key={day.value}
                            onClick={() => toggleDay(day.value)}
                            size="small"
                            type="button"
                            variant={active ? "contained" : "outlined"}
                          >
                            {day.label}
                          </Button>
                        );
                      })}
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ flexWrap: "wrap", mt: 0.5 }}
                    >
                      <Button
                        color="inherit"
                        onClick={() => setDays(EVERY_DAY)}
                        size="small"
                        type="button"
                      >
                        Every day
                      </Button>
                      <Button
                        color="inherit"
                        onClick={() => setDays(WEEKDAYS)}
                        size="small"
                        type="button"
                      >
                        Weekdays
                      </Button>
                      <Button
                        color="inherit"
                        onClick={() => setDays(WEEKEND_DAYS)}
                        size="small"
                        type="button"
                      >
                        Weekends
                      </Button>
                    </Stack>
                    {fieldMessage("days_of_week") ? (
                      <FormHelperText error>
                        {fieldMessage("days_of_week")}
                      </FormHelperText>
                    ) : null}
                  </Box>

                  <Controller
                    control={form.control}
                    name="all_day"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(_event, checked) =>
                              field.onChange(checked)
                            }
                            slotProps={{
                              input: { "aria-label": "All day" },
                            }}
                          />
                        }
                        label="All hours"
                      />
                    )}
                  />

                  {!allDay ? (
                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                        },
                      }}
                    >
                      <Controller
                        control={form.control}
                        name="start_time"
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                            label="Start time"
                            placeholder="18:00"
                            size="small"
                          />
                        )}
                      />
                      <Controller
                        control={form.control}
                        name="end_time"
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                            label="End time"
                            placeholder="22:00"
                            size="small"
                          />
                        )}
                      />
                    </Box>
                  ) : null}

                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                    }}
                  >
                    <Controller
                      control={form.control}
                      name="start_date"
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          label="Starts"
                          size="small"
                          slotProps={{
                            htmlInput: { "aria-label": "Start date" },
                            inputLabel: { shrink: true },
                          }}
                          type="date"
                        />
                      )}
                    />

                    <FormControl
                      component="fieldset"
                      error={Boolean(fieldMessage("end_date"))}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1.5,
                      }}
                    >
                      <FormLabel component="legend">Ends</FormLabel>
                      <RadioGroup
                        onChange={(_event, value) =>
                          handleEndsModeChange(value as "never" | "date")
                        }
                        row
                        value={endsMode}
                      >
                        <FormControlLabel
                          control={<Radio size="small" />}
                          label="Never"
                          value="never"
                        />
                        <FormControlLabel
                          control={<Radio size="small" />}
                          label="On date"
                          value="date"
                        />
                      </RadioGroup>
                      {endsMode === "date" ? (
                        <Controller
                          control={form.control}
                          name="end_date"
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              error={Boolean(fieldState.error)}
                              helperText={fieldState.error?.message}
                              size="small"
                              slotProps={{
                                htmlInput: { "aria-label": "End date" },
                              }}
                              type="date"
                            />
                          )}
                        />
                      ) : null}
                    </FormControl>
                  </Box>
                </Stack>
              </Box>
            ) : null}

            {/* ---- Who --------------------------------------------------- */}
            <Box component="fieldset" sx={{ border: 0, m: 0, p: 0 }}>
              <Typography
                component="legend"
                sx={{ fontWeight: 700, mb: 1.5 }}
                variant="body2"
              >
                Who
              </Typography>
              <Stack spacing={2}>
                <Controller
                  control={form.control}
                  name="membership_scope"
                  render={({ field, fieldState }) => (
                    <FormControl
                      error={Boolean(fieldState.error)}
                      fullWidth
                      size="small"
                    >
                      <InputLabel id="member-scope-label">
                        Member scope
                      </InputLabel>
                      <Select
                        {...field}
                        label="Member scope"
                        labelId="member-scope-label"
                        renderValue={(value) =>
                          MEMBERSHIP_SCOPE_TRIGGER_LABELS[value]
                        }
                      >
                        {MEMBERSHIP_SCOPES.map((scope) => (
                          <MenuItem key={scope} value={scope}>
                            {MEMBERSHIP_SCOPE_LABELS[scope]}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldState.error ? (
                        <FormHelperText>
                          {fieldState.error.message}
                        </FormHelperText>
                      ) : null}
                    </FormControl>
                  )}
                />

                {membershipScope === "specific_plans" ? (
                  <FormControl
                    error={Boolean(fieldMessage("membership_plan_ids"))}
                  >
                    <FormLabel>Eligible memberships</FormLabel>
                    <Paper sx={{ mt: 1, p: 1.5 }} variant="outlined">
                      <Stack spacing={0.5}>
                        {plans.length === 0 ? (
                          <Typography color="text.secondary" variant="body2">
                            No memberships at this facility.
                          </Typography>
                        ) : (
                          plans.map((plan) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedPlans.includes(plan.id)}
                                  onChange={() => togglePlan(plan.id)}
                                  size="small"
                                />
                              }
                              key={plan.id}
                              label={plan.name}
                            />
                          ))
                        )}
                      </Stack>
                    </Paper>
                    {fieldMessage("membership_plan_ids") ? (
                      <FormHelperText>
                        {fieldMessage("membership_plan_ids")}
                      </FormHelperText>
                    ) : null}
                  </FormControl>
                ) : null}
              </Stack>
            </Box>

            {/* ---- Where ------------------------------------------------- */}
            <Box component="fieldset" sx={{ border: 0, m: 0, p: 0 }}>
              <Typography
                component="legend"
                sx={{ fontWeight: 700, mb: 1.5 }}
                variant="body2"
              >
                Where
              </Typography>
              <Stack spacing={2}>
                <Controller
                  control={form.control}
                  name="service"
                  render={({ field, fieldState }) => (
                    <FormControl
                      error={Boolean(fieldState.error)}
                      fullWidth
                      size="small"
                    >
                      <InputLabel id="rate-service-label">Service</InputLabel>
                      <Select
                        {...field}
                        label="Service"
                        labelId="rate-service-label"
                      >
                        {PRICING_SERVICES.map((pricingService) => (
                          <MenuItem key={pricingService} value={pricingService}>
                            {SERVICE_LABELS[pricingService]}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldState.error ? (
                        <FormHelperText>
                          {fieldState.error.message}
                        </FormHelperText>
                      ) : null}
                    </FormControl>
                  )}
                />

                {isClasses ? (
                  <Controller
                    control={form.control}
                    name="offering_id"
                    render={({ field, fieldState }) => (
                      <Box>
                        <ClassOfferingPicker
                          facilityId={facilityId}
                          authorityGeneration={authorityGeneration}
                          offeringId={field.value}
                          disabled={saving}
                          onChange={field.onChange}
                        />
                        {fieldState.error ? (
                          <FormHelperText error>
                            {fieldState.error.message}
                          </FormHelperText>
                        ) : null}
                      </Box>
                    )}
                  />
                ) : (
                  <>
                    {courts.length > 0 ? (
                      <FormControl error={Boolean(fieldMessage("court_ids"))}>
                        <FormLabel>Courts</FormLabel>
                        <Paper sx={{ mt: 1, p: 1.5 }} variant="outlined">
                          <Stack direction="row" sx={{ flexWrap: "wrap" }}>
                            {courts.map((court) => (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedCourts.includes(court.id)}
                                    onChange={() => toggleCourt(court.id)}
                                    size="small"
                                  />
                                }
                                key={court.id}
                                label={court.name}
                              />
                            ))}
                          </Stack>
                        </Paper>
                        {fieldMessage("court_ids") ? (
                          <FormHelperText>
                            {fieldMessage("court_ids")}
                          </FormHelperText>
                        ) : null}
                      </FormControl>
                    ) : null}

                    <Controller
                      control={form.control}
                      name="court_tags"
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          error={Boolean(fieldState.error)}
                          fullWidth
                          helperText={fieldState.error?.message}
                          label="Court tags"
                          placeholder="premium, outdoor"
                          size="small"
                        />
                      )}
                    />
                  </>
                )}
              </Stack>
            </Box>

            {/* ---- Charge ------------------------------------------------ */}
            <Box component="fieldset" sx={{ border: 0, m: 0, p: 0 }}>
              <Typography
                component="legend"
                sx={{ fontWeight: 700, mb: 1.5 }}
                variant="body2"
              >
                Charge
              </Typography>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                  }}
                >
                  <Controller
                    control={form.control}
                    name="base_price"
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        label="Base price"
                        placeholder="45.00"
                        size="small"
                        slotProps={{ htmlInput: { inputMode: "decimal" } }}
                      />
                    )}
                  />
                  {!isClasses ? (
                    <Controller
                      control={form.control}
                      name="charge_unit"
                      render={({ field, fieldState }) => (
                        <FormControl
                          error={Boolean(fieldState.error)}
                          fullWidth
                          size="small"
                        >
                          <InputLabel id="charge-unit-label">
                            Charge unit
                          </InputLabel>
                          <Select
                            {...field}
                            label="Charge unit"
                            labelId="charge-unit-label"
                          >
                            {CHARGE_UNITS.map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {CHARGE_UNIT_LABELS[unit]}
                              </MenuItem>
                            ))}
                          </Select>
                          {fieldState.error ? (
                            <FormHelperText>
                              {fieldState.error.message}
                            </FormHelperText>
                          ) : null}
                        </FormControl>
                      )}
                    />
                  ) : null}
                </Box>

                {!isClasses ? (
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(3, minmax(0, 1fr))",
                      },
                    }}
                  >
                    <Controller
                      control={form.control}
                      name="included_player_count"
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          label="Included players"
                          size="small"
                          slotProps={{
                            htmlInput: {
                              "aria-label": "Included player count",
                            },
                          }}
                          type="number"
                        />
                      )}
                    />
                    <Controller
                      control={form.control}
                      name="extra_player_fee"
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          label="Extra player fee"
                          placeholder="5.00"
                          size="small"
                          slotProps={{
                            htmlInput: {
                              "aria-label": "Extra player fee",
                              inputMode: "decimal",
                            },
                          }}
                        />
                      )}
                    />
                    <Controller
                      control={form.control}
                      name="entry_fee"
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          label="Entry fee"
                          placeholder="10.00"
                          size="small"
                          slotProps={{
                            htmlInput: {
                              "aria-label": "Entry fee",
                              inputMode: "decimal",
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                ) : null}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={saving}
            onClick={() => onOpenChange(false)}
            size="small"
            type="button"
          >
            Cancel
          </Button>
          <Button
            disabled={saving}
            size="small"
            type="submit"
            variant="contained"
          >
            {saving ? "Saving…" : isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
